import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MotesLoginService } from '@motes-sdk/motes-login';
import { isWebflowTourLocale, Mote, MoteTranslation, MotesPaths, webflowLocaleForTourLanguage } from '@motes-sdk/models';

/**
 * Webflow Tour interface - represents a tour from Webflow CMS
 */
export interface wfTour {
    id?: string; // Webflow item ID
    cmsLocaleId: string;
    createdOn: string;
    fieldData: {
        city: string;
        country: string;
        'cover-image': {
            fileId: string;
            url: string;
            alt: string | null;
        };
        'duration-2': number;
        'entrance-fee': boolean;
        'extra-tour-images': Array<{
            fileId?: string;
            url?: string;
            alt?: string | null;
        }>;
        'has-quiz': boolean;
        'hide-tour': boolean;
        'is-free': boolean;
        key: string;
        language: string;
        'languages-2': string[];
        'lat-2': string;
        'long-2': string;
        'long-description-rich': string;
        name: string;
        'place-id': string;
        price: number;
        'price-eur-string': string;
        'price-string': string;
        'project-key': string;
        rating: number;
        'rating-count': number;
        'short-description': string;
        slug: string;
        'start-place': string;
        'story-count': number;
        tourlanguages: string;
        type: string;
    };
}

@Injectable({
    providedIn: 'root'
})
export class WebflowService {

    constructor(
        private http: HttpClient,
        private loginService: MotesLoginService
    ) {}

    /**
     * Fetch tours from Webflow API endpoint
     * @param locale Optional locale code (en, da, or de), defaults to en
     * @returns Array of Webflow tours
     */
    public async getWebflowTours(locale: string = 'en'): Promise<wfTour[]> {
        try {
            // Get auth headers
            const headers = await this.loginService.getAuthOptions();
            const normalizedLocale = isWebflowTourLocale(locale?.toLowerCase() ?? '') ? locale.toLowerCase() : 'en';

            // Call the Firebase Functions API endpoint
            const response: any = await this.http.get(
                `${MotesPaths.MOTES_REST}/v1/tours/actions/list-tours?locale=${normalizedLocale}`, 
                headers
            ).toPromise();
            
            console.log('🔍 Webflow Tours Response:', response);
            
            if (!response || !response.data) {
                console.error('Invalid response format:', response);
                return [];
            }

            return response.data as wfTour[];
        } catch (error) {
            console.error('Error fetching tours from Webflow:', error);
            throw error;
        }
    }

    /**
     * Fetch a single tour from Webflow API by Webflow ID
     * @param webflowId The Webflow item ID
     * @param locale Optional locale code (en, da, or de), defaults to en
     * @returns Webflow tour or null if not found
     */
    public async getWebflowTour(webflowId: string, locale: string = 'en'): Promise<wfTour | null> {
        try {
            const headers = await this.loginService.getAuthOptions();
            const normalizedLocale = isWebflowTourLocale(locale?.toLowerCase() ?? '') ? locale.toLowerCase() : 'en';

            const response: any = await this.http.get(
                `${MotesPaths.MOTES_REST}/v1/tours/actions/get-tour/${webflowId}?locale=${normalizedLocale}`, 
                headers
            ).toPromise();
            
            console.log('🔍 Webflow Tour Response:', response);
            
            if (response && response.data) {
                console.log('🔍 Webflow Tour Data:', response.data);
                return response.data as wfTour;
            } else {
                console.error('Invalid response format:', response);
                return null;
            }
        } catch (error) {
            console.error('Error fetching tour from Webflow:', error);
            return null;
        }
    }

    /**
     * Predict the Webflow share link from the tour key (used before first publish).
     * Slug matches the value written to Webflow on export.
     */
    public getAnticipatedShareLink(tour: Mote): string {
        if (!tour?.key) return '';
        return MotesPaths.WEBFLOW_TOUR_URL(this.webflowSlugFromTourKey(tour.key), tour.language);
    }

    /**
     * Resolve the public Webflow share link for a published tour.
     * Prefers Firebase marketingUrl (refreshed from live Webflow slug on each sync).
     * Falls back to live Webflow, then the legacy storyhunt.io tour page.
     */
    public async getPublishedShareLink(context: {
        tour: Mote;
        translations?: MoteTranslation[];
        projectMotes?: Mote[];
    }): Promise<string> {
        const { tour, translations, projectMotes } = context;
        if (!tour) return '';

        if (!tour.publishedOn) {
            return MotesPaths.CREATOR_TOUR_SHARE_LINK(tour);
        }

        if (tour.marketingUrl) {
            return tour.marketingUrl;
        }

        const webflowId = this.resolveWebflowId(tour, translations, projectMotes);
        if (!webflowId) {
            return MotesPaths.CREATOR_TOUR_SHARE_LINK(tour);
        }

        const locale = webflowLocaleForTourLanguage(tour.language);
        let wfTour = await this.getWebflowTour(webflowId, locale);
        let slug = wfTour?.fieldData?.slug;

        if (!slug && locale !== 'en') {
            wfTour = await this.getWebflowTour(webflowId, 'en');
            slug = wfTour?.fieldData?.slug;
        }

        if (slug) {
            return MotesPaths.WEBFLOW_TOUR_URL(slug, tour.language);
        }

        return MotesPaths.CREATOR_TOUR_SHARE_LINK(tour);
    }

    private resolveWebflowId(tour: Mote, translations?: MoteTranslation[], projectMotes?: Mote[]): string | null {
        if (tour.webflowId) return tour.webflowId;

        if (translations?.length && projectMotes?.length) {
            for (const translation of translations) {
                const translationMote = projectMotes.find(mote => mote.key === translation.moteKey);
                if (translationMote?.webflowId) return translationMote.webflowId;
            }
        }

        if (tour.originKey && projectMotes?.length) {
            const originMote = projectMotes.find(mote => mote.key === tour.originKey);
            if (originMote?.webflowId) return originMote.webflowId;
        }

        return null;
    }

    private webflowSlugFromTourKey(key: string): string {
        return key.replace(/_/g, '-').toLowerCase();
    }
}
